import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheck, HiExclamationCircle, HiX } from 'react-icons/hi';
import { Modal, Button } from '@/components/common';
import { DiscountVoucherUI } from '@/types/discountVoucher';
import { discountVoucherApi } from '@/api/endpoints/discountVouchers';
import { toast } from '@/store/toastStore';
import { formatCurrency } from '@/api/endpoints/receiptVouchers';

interface ConfirmDiscountVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  voucher: DiscountVoucherUI | null;
  action: 'confirm' | 'cancel' | 'delete';
}

const ConfirmDiscountVoucherModal: React.FC<ConfirmDiscountVoucherModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  voucher,
  action,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const getActionConfig = () => {
    switch (action) {
      case 'confirm':
        return {
          title: t('discountVoucher.modals.confirm.confirm.title', 'Xác nhận phiếu chiết khấu'),
          message: t('discountVoucher.modals.confirm.confirm.message', {
            code: voucher?.code,
          }),
          icon: <HiCheck className="w-6 h-6 text-green-600" />,
          confirmText: t('discountVoucher.actions.confirm', 'Xác nhận'),
          confirmClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          successMessage: t('discountVoucher.messages.confirmSuccess', 'Xác nhận phiếu chiết khấu thành công'),
        };
      case 'cancel':
        return {
          title: t('discountVoucher.modals.confirm.cancel.title', 'Hủy phiếu chiết khấu'),
          message: t('discountVoucher.modals.confirm.cancel.message', { code: voucher?.code }),
          icon: <HiX className="w-6 h-6 text-red-600" />,
          confirmText: t('common.agree', 'Đồng ý'),
          confirmClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          successMessage: t('discountVoucher.messages.cancelSuccess', 'Hủy phiếu chiết khấu thành công'),
        };
      case 'delete':
        return {
          title: t('discountVoucher.modals.confirm.delete.title', 'Xóa phiếu chiết khấu'),
          message: t('discountVoucher.modals.confirm.delete.message', { code: voucher?.code }),
          icon: <HiExclamationCircle className="w-6 h-6 text-red-600" />,
          confirmText: t('common.delete', 'Xóa'),
          confirmClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          successMessage: t('discountVoucher.messages.deleteSuccess', 'Xóa phiếu chiết khấu thành công'),
        };
      default:
        return {
          title: '',
          message: '',
          icon: null,
          confirmText: '',
          confirmClass: '',
          successMessage: '',
        };
    }
  };

  const handleConfirm = async () => {
    if (!voucher) return;

    setLoading(true);
    try {
      switch (action) {
        case 'confirm':
          // Backend will automatically update customer debt and total_sell
          await discountVoucherApi.confirmDiscountVoucher(voucher.code);
          break;
        case 'cancel':
          await discountVoucherApi.cancelDiscountVoucher(voucher.code);
          break;
        case 'delete':
          await discountVoucherApi.deleteDiscountVoucher(voucher.code);
          break;
      }

      const config = getActionConfig();
      toast.success(config.successMessage);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error(`Error ${action} discount voucher:`, error);
      const errorMessage = error?.response?.data?.message;
      toast.error(
        errorMessage ||
          t(`discountVoucher.messages.${action}Error`, 'Thao tác với phiếu chiết khấu thất bại'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!voucher) return null;

  const config = getActionConfig();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={config.title} size="md">
      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">{config.icon}</div>
          <div className="flex-1">
            <p className="text-sm text-gray-700 mb-4">{config.message}</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">
                    {t('discountVoucher.list.customer', 'Khách hàng')}:
                  </span>
                  <span className="ml-2 text-gray-900">{voucher.customer_id}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    {t('discountVoucher.list.discount', 'Chiết khấu')}:
                  </span>
                  <span className="ml-2 text-gray-900">
                    {voucher.discount_type === 'percent'
                      ? `${voucher.discount_value}%`
                      : formatCurrency(voucher.discount_value)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    {t('discountVoucher.list.totalDiscount', 'Tổng CK áp dụng')}:
                  </span>
                  <span className="ml-2 text-green-600 font-medium">
                    {formatCurrency(voucher.total_discount)}
                  </span>
                </div>
              </div>
            </div>

            {(action === 'confirm' || action === 'delete') && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <div className="flex">
                  <HiExclamationCircle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      {action === 'confirm'
                        ? t(
                            'discountVoucher.modals.confirm.confirm.warning',
                            'Sau khi xác nhận, không thể chỉnh sửa phiếu chiết khấu.',
                          )
                        : t(
                            'discountVoucher.modals.confirm.delete.warning',
                            'Thao tác xóa không thể hoàn tác.',
                          )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={loading}
            className={`text-white ${config.confirmClass}`}
          >
            {config.confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDiscountVoucherModal;
